import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import './ContactWidget.css';

function timeAgo(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleString('th-TH', { dateStyle: 'short', timeStyle: 'short' });
}

// ประเภทคำร้อง — ต้องตรงกับ CONTACT_CATEGORIES ฝั่ง server
const CATEGORIES = [
    { key: 'position', label: 'ขอแก้ชื่อตำแหน่ง', evidence: true },
    { key: 'name', label: 'ขอแก้ชื่อ-สกุล / ชื่อผิด', evidence: true },
    { key: 'salary', label: 'ขอแก้ข้อมูลเงินเดือน', evidence: true },
    { key: 'document', label: 'สอบถามเรื่องเอกสาร', evidence: false },
    { key: 'general', label: 'อื่นๆ / สอบถามทั่วไป', evidence: false }
];
const CATEGORY_LABELS = CATEGORIES.reduce((acc, c) => { acc[c.key] = c.label; return acc; }, {});
const MAX_FILES = 5;
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const ALLOWED_EXT_HINT = '.jpg,.jpeg,.png,.webp,.gif,.pdf';

function parseAttachments(raw) {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try { return JSON.parse(raw) || []; } catch (e) { return []; }
}

export default function ContactWidget() {
    const [open, setOpen] = useState(false);
    const [status, setStatus] = useState({ email: '', email_verified: false });
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState('');
    const [category, setCategory] = useState('general');
    const [files, setFiles] = useState([]);
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState({ type: '', text: '' });
    const [loaded, setLoaded] = useState(false);

    const loadData = useCallback(async () => {
        try {
            const [s, m] = await Promise.all([
                api.get('/contact/status'),
                api.get('/contact/my').catch(() => ({ data: [] }))
            ]);
            setStatus(s.data);
            setMessages(m.data || []);
        } catch (e) { /* ignore */ }
        finally { setLoaded(true); }
    }, []);

    useEffect(() => {
        if (open) loadData();
    }, [open, loadData]);

    const onPickFiles = (e) => {
        const picked = Array.from(e.target.files || []);
        e.target.value = ''; // reset เพื่อให้เลือกไฟล์เดิมซ้ำได้
        if (!picked.length) return;
        setResult({ type: '', text: '' });
        const next = [...files];
        for (const f of picked) {
            if (next.length >= MAX_FILES) {
                setResult({ type: 'error', text: `แนบได้สูงสุด ${MAX_FILES} ไฟล์` });
                break;
            }
            if (f.size > MAX_FILE_BYTES) {
                setResult({ type: 'error', text: `"${f.name}" ใหญ่เกิน 5MB` });
                continue;
            }
            // กันไฟล์ซ้ำ (ชื่อ+ขนาด)
            if (next.some(x => x.name === f.name && x.size === f.size)) continue;
            next.push(f);
        }
        setFiles(next.slice(0, MAX_FILES));
    };

    const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

    const send = async () => {
        if (!text.trim()) {
            setResult({ type: 'error', text: 'กรุณาพิมพ์ข้อความ' });
            return;
        }
        setSending(true);
        setResult({ type: '', text: '' });
        try {
            const fd = new FormData();
            fd.append('message', text.trim());
            fd.append('category', category);
            files.forEach(f => fd.append('attachments', f));
            const res = await api.post('/contact', fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setResult({ type: 'success', text: res.data.message || 'ส่งข้อความแล้ว' });
            setText('');
            setFiles([]);
            setCategory('general');
            loadData();
        } catch (err) {
            setResult({ type: 'error', text: err.response?.data?.message || err.response?.data?.error || 'ส่งไม่สำเร็จ' });
        } finally {
            setSending(false);
        }
    };

    const activeCat = CATEGORIES.find(c => c.key === category);

    return (
        <>
            <button className="contact-fab" onClick={() => setOpen(true)} title="ติดต่อเจ้าหน้าที่" aria-label="ติดต่อเจ้าหน้าที่">
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                <span className="contact-fab-label">ติดต่อเจ้าหน้าที่</span>
            </button>

            {open && (
                <div className="contact-overlay" onClick={() => setOpen(false)}>
                    <div className="contact-panel" onClick={(e) => e.stopPropagation()}>
                        <div className="contact-panel-head">
                            <h3>ติดต่อเจ้าหน้าที่</h3>
                            <button className="contact-close" onClick={() => setOpen(false)}>&times;</button>
                        </div>

                        {!loaded ? (
                            <div className="contact-loading"><div className="spinner"></div></div>
                        ) : !status.email_verified ? (
                            <div className="contact-verify-cta">
                                <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.6">
                                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
                                </svg>
                                <p><strong>ต้องยืนยันอีเมลก่อน</strong></p>
                                <p className="contact-verify-desc">
                                    เจ้าหน้าที่จะตอบกลับข้อความของคุณทาง<strong>อีเมล</strong> จึงต้องยืนยันอีเมลให้เรียบร้อยก่อนใช้งาน
                                </p>
                                <Link to="/profile#email" className="steam-btn steam-btn-primary" onClick={() => setOpen(false)}>
                                    ไปยืนยันอีเมล
                                </Link>
                            </div>
                        ) : (
                            <div className="contact-body">
                                <div className="contact-compose">
                                    <label className="contact-field-label">ประเภทคำร้อง</label>
                                    <select
                                        className="contact-category-select"
                                        value={category}
                                        onChange={(e) => setCategory(e.target.value)}
                                    >
                                        {CATEGORIES.map(c => (
                                            <option key={c.key} value={c.key}>{c.label}</option>
                                        ))}
                                    </select>

                                    <textarea
                                        value={text}
                                        onChange={(e) => setText(e.target.value.slice(0, 1000))}
                                        placeholder={
                                            category === 'position' ? 'ระบุชื่อตำแหน่งที่ถูกต้องที่ต้องการแก้...'
                                                : category === 'name' ? 'ระบุชื่อ-สกุลที่ถูกต้องที่ต้องการแก้...'
                                                : 'พิมพ์ข้อความถึงเจ้าหน้าที่... (โปรดใช้ถ้อยคำสุภาพ)'
                                        }
                                        rows="3"
                                        maxLength="1000"
                                    />

                                    {/* แนบไฟล์หลักฐาน */}
                                    <div className="contact-attach">
                                        <label className="contact-attach-btn">
                                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                                            </svg>
                                            แนบหลักฐาน
                                            <input
                                                type="file"
                                                accept={ALLOWED_EXT_HINT}
                                                multiple
                                                onChange={onPickFiles}
                                                disabled={files.length >= MAX_FILES}
                                                hidden
                                            />
                                        </label>
                                        <span className="contact-attach-hint">
                                            {activeCat?.evidence ? 'แนบรูป/PDF ประกอบการพิจารณา · ' : ''}สูงสุด {MAX_FILES} ไฟล์ · ไฟล์ละ 5MB
                                        </span>
                                    </div>
                                    {files.length > 0 && (
                                        <ul className="contact-file-list">
                                            {files.map((f, i) => (
                                                <li key={i} className="contact-file-item">
                                                    <span className="contact-file-name" title={f.name}>
                                                        {f.type === 'application/pdf' ? '📄' : '🖼️'} {f.name}
                                                    </span>
                                                    <button type="button" className="contact-file-remove" onClick={() => removeFile(i)} aria-label="ลบไฟล์">&times;</button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}

                                    <div className="contact-compose-foot">
                                        <span className="contact-count">{text.length}/1000</span>
                                        <button className="steam-btn steam-btn-primary" onClick={send} disabled={sending}>
                                            {sending ? 'กำลังส่ง...' : 'ส่งข้อความ'}
                                        </button>
                                    </div>
                                    {result.text && (
                                        <div className={`contact-result ${result.type}`}>{result.text}</div>
                                    )}
                                    <p className="contact-reply-note">เจ้าหน้าที่จะตอบกลับทางอีเมล: {status.email}</p>
                                </div>

                                <div className="contact-history">
                                    <div className="contact-history-title">ข้อความที่เคยส่ง</div>
                                    {messages.length === 0 ? (
                                        <p className="contact-empty">ยังไม่มีข้อความ</p>
                                    ) : messages.map(m => {
                                        const atts = parseAttachments(m.attachments);
                                        return (
                                        <div key={m.id} className="contact-item">
                                            <div className="contact-item-head">
                                                <span className="contact-time">{timeAgo(m.created_at)}</span>
                                                {m.status === 'replied'
                                                    ? <span className="contact-badge replied">ตอบแล้ว</span>
                                                    : <span className="contact-badge new">รอเจ้าหน้าที่ตอบ</span>}
                                            </div>
                                            {m.category && m.category !== 'general' && (
                                                <span className="contact-cat-tag">{CATEGORY_LABELS[m.category] || m.category}</span>
                                            )}
                                            <div className="contact-msg">{m.message}</div>
                                            {atts.length > 0 && (
                                                <div className="contact-att-list">
                                                    {atts.map((a, i) => (
                                                        <a key={i} href={a.url} target="_blank" rel="noopener noreferrer" className="contact-att-link" title={a.name}>
                                                            {a.type === 'application/pdf' ? '📄' : '🖼️'} {a.name}
                                                        </a>
                                                    ))}
                                                </div>
                                            )}
                                            {m.status === 'replied' && m.reply && (
                                                <div className="contact-reply">
                                                    <span className="contact-reply-label">
                                                        ตอบกลับ{m.replied_by_name ? ` โดย ${m.replied_by_name}` : ''} (ส่งไปทางอีเมลด้วย):
                                                    </span>
                                                    <div className="contact-reply-text">{m.reply}</div>
                                                </div>
                                            )}
                                        </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
